import { useMemo, useState, useEffect } from 'react';
import clsx from 'clsx';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import useScheduleData from '../hooks/useScheduleData';
import EmployeeRow from './EmployeeRow';

dayjs.extend(customParseFormat);

dayjs.extend(isoWeek);

export default function CalendarView({ businessId }) {
  const [periodOffset, setPeriodOffset] = useState(0);
  const {
    loading, error, providers, range,
    minConfig, scheduleMap, headCount, timeOff, rules, reload,
  } = useScheduleData(businessId, periodOffset);

  const [showWeekends, setShowWeekends] = useState(true);

  /* ───── date list for the grid ───── */
  const days = useMemo(() => {
    if (!range.start) return [];
    const diff = range.end.diff(range.start, 'day') + 1;
    return Array.from({ length: diff }, (_, i) => range.start.add(i, 'day'));
  }, [range]);

  /* ───── refresh after save ───── */
  useEffect(() => {
    const h = () => reload();
    window.addEventListener('schedule-updated', h);
    return () => window.removeEventListener('schedule-updated', h);
  }, [reload]);

  /* ───── helper tables ───── */
  const offMap = useMemo(() => {
    const m = {};
    timeOff
      .filter(r => r.status !== 'REJECTED')  // skip rejected entries
      .forEach(r => {
        const start = dayjs(r.start_date);
        const end   = dayjs(r.end_date || r.start_date);
        for (let d = start.clone(); !d.isAfter(end, 'day'); d = d.add(1, 'day')) {
          m[`${r.employee_id}-${d.format('YYYY-MM-DD')}`] = r.type; // TIME_OFF | SICK
        }
      });

    return m;
  }, [timeOff]);

  console.log(offMap);

  const minByDow = minConfig ? {
    1: minConfig.mon_min,
    2: minConfig.tue_min,
    3: minConfig.wed_min,
    4: minConfig.thu_min,
    5: minConfig.fri_min,
    6: minConfig.sat_min,
    7: minConfig.sun_min,
  } : {};

  const pickRuleFor = (empId) => {
    if (!rules?.length) return {};
    return (
      rules.find(r => Array.isArray(r.employees) && r.employees.includes(empId)) // explicit rule
      ?? rules.find(r => !Array.isArray(r.employees) || r.employees.length === 0) // company rule
      ?? {}
    );
  };
  /* ───── employee stats (skip leave days) ───── */
  const employeeStats = useMemo(() => {
    const stats = {};

    providers.forEach(emp => {
      /* ——— thresholds for THIS employee ——— */
      const { min_hours_per_week = 0,
              max_hours_per_week = Number.POSITIVE_INFINITY,
              min_days_off_per_week = 0 } = pickRuleFor(emp.id);
      const empSchedule  = scheduleMap[emp.id] || {};
      const weekInfo     = {};
      Object.entries(empSchedule).forEach(([iso, shifts]) => {
        if (offMap[`${emp.id}-${iso}`] || !shifts.length) return; // skip leave
  
        const dayObj  = dayjs(iso);
        const wkKey = `${dayObj.isoWeekYear()}-W${String(dayObj.isoWeek()).padStart(2,'0')}`;
        const wkEnd = dayObj.endOf('isoWeek');
        if (!weekInfo[wkKey]) weekInfo[wkKey] = { mins:0, days:new Set(), end:wkEnd };

        shifts.forEach(s => {
          const mins =
            dayjs(s.end, 'HH:mm').diff(dayjs(s.start, 'HH:mm'), 'minute') -
            (s.breaks || []).reduce(
              (sum, b) => sum + dayjs(b.end, 'HH:mm').diff(dayjs(b.start, 'HH:mm'), 'minute'),
              0,
            );
          weekInfo[wkKey].mins += mins;
        });
        weekInfo[wkKey].days.add(iso);
      });
  
      let status = 'ok';
      let daysOffWarn = false;
      Object.values(weekInfo).forEach(({ mins, days, end }) => {
        /* skip weeks whose Sunday is beyond this window – they’re still “in play” */
        if (end.isAfter(range.end)) return;
   
        const hrs = mins / 60;
        if (hrs < min_hours_per_week)             status = 'under';
        else if (hrs > max_hours_per_week && status !== 'under') status = 'over';

        const daysWorked = days.size;
        if (7 - daysWorked < min_days_off_per_week) daysOffWarn = true;
      });
      if (Object.keys(weekInfo).length === 0 && min_hours_per_week > 0) {
        status = 'under';
      }
      stats[emp.id] = { status, daysOffWarn };
    });    return stats;
  }, [providers, scheduleMap, range.end, rules, offMap]);

  /* ───── head-count adjusted for leave ───── */
  const headCountAvail = useMemo(() => {
    const hc = {};
    providers.forEach(emp => {
      const empSchedule = scheduleMap[emp.id] || {};
      Object.entries(empSchedule).forEach(([iso, shifts]) => {
        if (offMap[`${emp.id}-${iso}`] || !shifts.length) return;
        hc[iso] = (hc[iso] || 0) + 1;
      });
    });
    return hc;
  }, [providers, scheduleMap, offMap]);

  /* ───── ui guards ───── */
  if (loading) return <p>Loading …</p>;
  if (error)   return <p className="text-red-600">{error}</p>;

  /* ───── helpers ───── */
  const movePeriod = d => setPeriodOffset(p => p + d);

  return (
    <div>
      {/* ─── period header ─── */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center space-x-2">
          <button onClick={() => movePeriod(-1)} className="px-2 py-1 bg-gray-200 hover:bg-gray-300 rounded">‹</button>
          <h2 className="text-lg font-medium">
            {range.start.format('DD MMM')} – {range.end.format('DD MMM YYYY')}
          </h2>
          <button onClick={() => movePeriod(1)}  className="px-2 py-1 bg-gray-200 hover:bg-gray-300 rounded">›</button>
        </div>

        <label className="flex items-center space-x-2 text-sm">
          <input
            type="checkbox"
            checked={showWeekends}
            onChange={() => setShowWeekends(s => !s)}
          />
          <span>Show weekends</span>
        </label>
      </div>

      {/* ─── legend ─── */}
      <div className="flex items-center space-x-5 text-xs mb-3">
        <span className="flex items-center space-x-1">
          <span className="w-3 h-3 bg-red-50   border border-red-200   inline-block" />
          <span>&lt; min hrs</span>
        </span>
        <span className="flex items-center space-x-1">
          <span className="w-3 h-3 bg-green-50 border border-green-200 inline-block" />
          <span>within range</span>
        </span>
        <span className="flex items-center space-x-1">
          <span className="w-3 h-3 bg-yellow-50 border border-yellow-200 inline-block" />
          <span>&gt; max hrs</span>
        </span>
        <span className="flex items-center space-x-1">
          <span className="w-3 h-3 bg-red-600 inline-block" />
          <span>LOW staff</span>
        </span>
        <span className="flex items-center space-x-1">
          <span className="w-3 h-3 bg-orange-300 inline-block" />
          <span>Sick leave</span>
        </span>
        <span className="flex items-center space-x-1">
          <span className="w-3 h-3 bg-teal-300 inline-block" />
          <span>Time-off</span>
        </span>
      </div>

      {/* ─── grid header ─── */}
      <div
        className="grid"
        style={{ gridTemplateColumns: `200px repeat(${days.length},1fr)` }}
      >
        <div className="border-r border-b px-2 py-1 bg-gray-50" />
        {days.map(d => {
          const iso   = d.format('YYYY-MM-DD');
          const dow   = d.day() === 0 ? 7 : d.day();
          const min   = minByDow[dow] ?? 0;
          const hc    = headCountAvail[iso] || 0;

          let label   = '';
          let colour  = '';
          if (hc < min)           { label = 'LOW';  colour = 'text-red-600'; }
          else if (hc === min)    { label = 'OK';   colour = 'text-yellow-600'; }
          else                    { label = 'HIGH'; colour = 'text-green-600'; }

          return (
            <div
              key={d.toString()}
              className={clsx(
                'border-b px-1 py-1 flex flex-col items-center justify-center text-sm font-medium',
                (!showWeekends && (d.day() === 0 || d.day() === 6)) && 'hidden',
              )}
            >
              <span>{d.format('ddd DD')}</span>
              {min > 0 && <span className={`text-[10px] leading-none ${colour}`}>{label}</span>}
            </div>
          );
        })}
      </div>

      {/* ─── employee rows ─── */}
      {providers.map(emp => (
        <EmployeeRow
          key={emp.id}
          employee={emp}
          days={days}
          hideWeekend={!showWeekends}
          minByDow={minByDow}
          schedule={scheduleMap[emp.id] || {}}
          headCountByDate={headCountAvail}
          businessId={businessId}
          offMap={offMap}
          rowStatus={employeeStats[emp.id]?.status}
          daysOffWarn={employeeStats[emp.id]?.daysOffWarn}
        />
      ))}
    </div>
  );
}
